import { useCallback, useEffect, useMemo, useState } from 'react'
import Joyride, { EVENTS, STATUS } from 'react-joyride'
import { useSidebar } from '@/context/SidebarContext'
import { deviceDetector } from '@/helpers/deviceDetector'

const joyrideStyles = {
  options: {
    arrowColor: 'rgba(67, 89, 249, 1)',
    backgroundColor: 'rgba(67, 89, 249, 1)',
    beaconSize: 36,
    overlayColor: 'rgba(0, 0, 0, 0.5)',
    primaryColor: '#1ab395',
    spotlightShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
    textColor: '#ffffff',
    zIndex: 100,
    radius: '8px',
  },
  tooltip: {
    placement: 'auto',
    padding: '1.25rem',
    maxWidth: 'calc(100vh/13.75rem)',
    maxHeight: 'calc(100vw/16.875rem)',
    fontSize: '15px',
  },
  tooltipTitle: {
    fontSize: '16px',
  },
  buttonBack: {
    color: 'red',
    marginRight: 10,
  },
}

export default function useTour(steps, localStorageKey) {
  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const { setLeftSidebarIsHidden } = useSidebar()
  const [tourCompleted, setTourCompleted] = useState(false)

  const isMobile = deviceDetector()

  useEffect(() => {
    const tourViewed = localStorageKey && window.localStorage.getItem(localStorageKey)

    if (!tourViewed) {
      window.localStorage.setItem(localStorageKey, '1')
      setRun(true)
    } else {
      setTourCompleted(true)
    }
  }, [localStorageKey])

  useEffect(() => {
    if (!isMobile) return
    const btn = document.querySelector('.calendarTriggerBtn')
    const handleClick = () => {
      if (tourCompleted) return
      setRun(false)
      setLeftSidebarIsHidden(true)
      setTimeout(() => {
        setStepIndex(2)
        setRun(true)
      }, 400)
    }
    btn?.addEventListener('click', handleClick)
    return () => btn?.removeEventListener('click', handleClick)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setLeftSidebarIsHidden, tourCompleted])

  const handleJoyrideCallback = useCallback(
    (data) => {
      const { status, index, type } = data

      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        setTourCompleted(true)
        window.dispatchEvent(new Event('tourFinished'))

        setRun(false)
        setStepIndex(0)
        return
      }

      if (type === EVENTS.STEP_AFTER) {
        // Paso 1: abrir sidebar automáticamente (solo mobile)
        if (index === 1) {
          if (isMobile) {
            const btn = document.querySelector('.calendarTriggerBtn')
            if (btn) {
              btn.click()
            }

            setTimeout(() => {
              setStepIndex(2)
              setRun(true)
            }, 500)
            return
          }

          // Desktop: avanzar directo al paso 2
          setStepIndex(2)
          return
        }

        // Paso 2: ocultar sidebar antes de avanzar (solo mobile)
        if (index === 2) {
          if (isMobile) {
            setLeftSidebarIsHidden(true)
            setTimeout(() => {
              setStepIndex(3)
            }, 300)
          } else {
            setStepIndex(3)
          }
          return
        }

        // Avance normal
        setStepIndex((prev) => prev + 1)
      }

      if (type === EVENTS.TARGET_NOT_FOUND || type === EVENTS.STEP_BEFORE) {
        setStepIndex(index)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setLeftSidebarIsHidden],
  )

  const tour = useMemo(
    () => (
      <Joyride
        callback={handleJoyrideCallback}
        continuous
        run={run}
        scrollToFirstStep
        showSkipButton
        showProgress={false}
        hideCloseButton
        hideBackButton
        steps={steps}
        stepIndex={stepIndex}
        styles={joyrideStyles}
        locale={{ skip: 'Saltar', next: 'Siguiente', last: 'Finalizar' }}
        showOverlay={stepIndex !== 0}
        disableBeacon={true}
      />
    ),
    [steps, stepIndex, handleJoyrideCallback, run],
  )

  return tour
}
